import moment from "moment";
import { request } from "../services/axios";
import { orderBy, toLower } from "lodash";

// Users restricted to the commentary panel only. They may open the commentary
// matches pages (and the match commentary page they link to) and nothing else;
// every other page shows a not-authorised message, and page-level actions such
// as "New Player" are hidden for them.
export const COMMENTARY_ONLY_USER_IDS = ["56", "57", "13"];
// "/commentary/matches" also covers the commentary comments page at
// "/commentary/matches/comments" via the startsWith check.
export const COMMENTARY_ALLOWED_PATHS = ["/commentary/matches"];
export const isCommentaryOnlyUser = (auth) =>
  COMMENTARY_ONLY_USER_IDS.includes(String(auth?.id));

export const getGenderOptions = (sport) => {
  let genders = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
  ];

  if (
    toLower(sport) === "tennis" ||
    toLower(sport) === "golf" ||
    toLower(sport) === "table-tennis" ||
    toLower(sport) === "mma" ||
    toLower(sport) === "motorsport"
  ) {
    genders?.push({ value: "Mixed", label: "Mixed" });
  }

  return genders;
};

export const getBestOfSetsOptions = (sport) => {
  if (sport === "tennis") {
    return [
      { value: "3", label: "3" },
      { value: "5", label: "5" },
    ];
  }

  if (sport === "table-tennis") {
    return [
      { value: "5", label: "5" },
      { value: "7", label: "7" },
    ];
  }
};

export const getCountries = async () => {
  const resp = await request({
    url: `/countries?size=500&sort=name`,
    method: "get",
  });

  let countries = resp?.data?.content?.map((c) => ({
    label: c?.name,
    value: c?.id,
    theSportsId: c?.mapping?.theSports, // TheSports country ID (for TheSports API)
  }));
  return countries;
};

export const getLangauges = async () => {
  const resp = await request({
    url: `/languages?size=500`,
    method: "get",
  });

  let countries = resp?.data?.content?.map((c) => ({
    label: c?.name,
    value: c?.id,
  }));
  return countries;
};

export const getSeasons = async () => {
  const resp = await request({
    url: `/seasons?size=500`,
    method: "get",
  });

  let seasons = resp?.data?.content?.map((c) => ({
    label: c?.name,
    value: c?.id,
  }));
  return seasons;
};

export const slugify = (str) => {
  return str
    ?.toLowerCase()
    ?.replace(/\s+/g, "-")
    ?.replace(/\//g, "-")
    ?.replace(/-+/g, "-")
    ?.replace(/^-+|-+$/g, "");
};

export const matchOfficialPositionOptions = [
  { value: "Referee", label: "Referee" },
  { value: "Delegate", label: "Delegate" },
];

export const circketOfficialPositionOptions = [
  { value: "Referee", label: "Referee" },
  { value: "Umpire", label: "Umpire" },
];

export const cricketBattingStyleOptons = [
  { value: "Right-hand bat", label: "Right-hand bat" },
  { value: "Left-hand bat", label: "Left-hand bat" },
  { value: "Neutral", label: "Neutral" },
  { value: "Left and right hand", label: "Left and right hand" },
];
export const cricketBowlingStyleOptions = [
  { value: "Right-arm medium-fast", label: "Right-arm medium-fast" },
  { value: "Right-arm fast-medium", label: "Right-arm fast-medium" },
  { value: "Right-arm fast", label: "Right-arm fast" },
  { value: "Right-arm medium", label: "Right-arm medium" },
  { value: "Right-arm leg-break", label: "Right-arm leg-break" },
  { value: "Right-arm off-spin", label: "Right-arm off-spin" },
  { value: "Left-arm fast", label: "Left-arm fast" },
  { value: "Left-arm fast-medium", label: "Left-arm fast-medium" },
  { value: "Left-arm medium-fast", label: "Left-arm medium-fast" },
  { value: "Left-arm leg-break", label: "Left-arm leg-break" },
  { value: "Left-arm off-spin", label: "Left-arm off-spin" },
  { value: "Left-arm medium", label: "Left-arm medium" },
  { value: "Slow left-arm orthodox", label: "Slow left-arm orthodox" },
  { value: "Left-arm slow", label: "Left-arm slow" },
  { value: "Right-arm Leg-break googly", label: "Right-arm Leg-break googly" },
  { value: "Slow left-arm chinaman", label: "Slow left-arm chinaman" },
  { value: "Right-arm slow", label: "Right-arm slow" },
  { value: "Right-arm off-break", label: "Right-arm off-break" },
  { value: "Left-arm Leg-break googly", label: "Left-arm Leg-break googly" },
  { value: "Right-arm slow-medium", label: "Right-arm slow-medium" },
  { value: "Leg-break googly", label: "Leg-break googly" },
  { value: "Leg-break", label: "Leg-break" },
];

export const formatPageTitle = (str) => {
  if (!str) return "";

  const cleaned = str?.replace(/-/g, " "); // replace all '-' with spaces
  return cleaned?.charAt(0)?.toUpperCase() + cleaned?.slice(1);
};

const formatPresentCustomDate = (presentDate) => {
  if (!presentDate) return;
  let date = new Date(presentDate);

  let day = date.getDate();
  let month = date.getMonth() + 1;
  let year = date.getFullYear();

  let str = `${day < 10 ? "0" + day : day}-${
    month < 10 ? "0" + month : month
  }-${year}`;

  return str;
};

const formatCustomDateToApi = (customDate) => {
  let date = `${customDate?.split("-")?.[2]}-${customDate?.split("-")?.[1]}-${
    customDate.split("-")[0]
  }`;
  return date;
};

export const venuesOption = [
  { value: "Indoor", label: "Indoor" },
  { value: "Outdoor", label: "Outdoor" },
  { value: "Outdoor with floodlights", label: "Outdoor with floodlights" },
  { value: "Retractable roof", label: "Retractable roof" },
];

export const getPlayerPositionOptions = (sport) => {
  let options = [];

  switch (sport) {
    case "football":
      options = [
        { value: "Goalkeeper", label: "Goalkeeper" },
        { value: "Defender", label: "Defender" },
        { value: "Midfielder", label: "Midfielder" },
        { value: "Striker", label: "Striker" },
        { value: "Bench", label: "Bench" },
      ];
      break;
    case "basketball":
      options = [
        { value: "Small forward", label: "Small forward" },
        { value: "Bench", label: "Bench" },
        { value: "Guard", label: "Guard" },
        { value: "Center", label: "Center" },
        { value: "Power forward", label: "Power forward" },
        { value: "Shooting guard", label: "Shooting guard" },
      ];
      break;

    case "american-football":
      options = [
        { value: "Point guard", label: "Point guard" },
        { value: "Shooting guard", label: "Shooting guard" },
        { value: "Small forward", label: "Small forward" },
        { value: "Power forward", label: "Power forward" },
        { value: "Center", label: "Center" },
        { value: "Bench", label: "Bench" },
      ];
      break;

    case "hockey":
      options = [
        { value: "Center", label: "Center" },
        { value: "Right wing", label: "Right wing" },
        { value: "Bench", label: "Bench" },
        { value: "Goaltender", label: "Goaltender" },
        { value: "Left wing", label: "Left wing" },
        { value: "Defender", label: "Defender" },
        { value: "Bench", label: "Bench" },
      ];
      break;

    case "rugby-league":
      options = [
        { value: "Prop", label: "Prop" },
        { value: "Hooker", label: "Hooker" },
        { value: "Lock", label: "Lock" },
        { value: "Flanker", label: "Flanker" },
        { value: "Number 8", label: "Number 8" },
        { value: "Scrum half", label: "Scrum half" },
        { value: "Fly half", label: "Fly half" },
        { value: "Winger", label: "Winger" },
        { value: "Center", label: "Center" },
        { value: "Full back", label: "Full back" },
        { value: "Bench", label: "Bench" },
      ];
      break;

    case "rugby-union":
      options = [
        { value: "Prop", label: "Prop" },
        { value: "Hooker", label: "Hooker" },
        { value: "Lock", label: "Lock" },
        { value: "Flanker", label: "Flanker" },
        { value: "Number 8", label: "Number 8" },
        { value: "Scrum half", label: "Scrum half" },
        { value: "Fly half", label: "Fly half" },
        { value: "Winger", label: "Winger" },
        { value: "Center", label: "Center" },
        { value: "Full back", label: "Full back" },
        { value: "Bench", label: "Bench" },
      ];
      break;
    case "cricket":
      options = [
        { value: "Batsman", label: "Bastman" },
        { value: "Bowler", label: "Bowler" },
        { value: "All-rounder", label: "All-rounder" },
        { value: "Wicket-keeper", label: "Wicket-keeper" },
      ];
      break;
    case "baseball":
      options = [
        { value: "Batter", label: "Batter" },
        { value: "Pitcher", label: "Pitcher" },
        // { value: "Catcher", label: "Catcher" },
        { value: "Substitute pitcher", label: "Substitute pitcher" },
        { value: "Bench", label: "Bench" },
        // { value: "Shortstop", label: "Shortstop" },
        // { value: "First base", label: "First base" },
        // { value: "Second base", label: "Second base" },
        // { value: "Third base", label: "Third base" },
        // { value: "Outfield", label: "Outfield" },
        // { value: "Infield", label: "Infield" },
        // { value: "Center outfield", label: "Center outfield" },
        // { value: "Reliever", label: "Reliever" },
      ];
    default:
      break;
  }

  return options;
};

export const getMatchStatusFilterOptions = (sport) => {
  let options = [];

  switch (sport) {
    case "football":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st half", label: "1st half" },
        { value: "Half time", label: "Half time" },
        { value: "2nd half", label: "2nd half" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "To finish", label: "To finish" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "ET 1st half", label: "Extra Time First Half" },
        { value: "ET 2nd half", label: "Extra Time Second Half" },
        { value: "Penalties", label: "Penalties" },
        { value: "Finished AP", label: "Finished After Penalties" },
        { value: "Finished AET", label: "Finished After Extra Time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
        { value: "Unknown", label: "Unknown" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
        { value: "Awaiting info", label: "Awaiting info" },
      ];
      break;
    case "mma":
      options = [
        { label: "Not started yet", value: "Not started yet" },

        { label: "1st round", value: "1st round" },
        { label: "2nd round", value: "2nd round" },
        { label: "3rd round", value: "3rd round" },
        { label: "4th round", value: "4th round" },
        { label: "5th round", value: "5th round" },

        { label: "1st break", value: "1st break" },
        { label: "2nd break", value: "2nd break" },
        { label: "3rd break", value: "3rd break" },
        { label: "4th break", value: "4th break" },

        { label: "Interrupted", value: "Interrupted" },
        { label: "Finished", value: "Finished" },
        { label: "Cancelled", value: "Cancelled" },
        { label: "Deleted", value: "Deleted" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
        { value: "Awaiting info", label: "Awaiting info" },
      ];
      break;
    case "basketball":
      options = [
        { value: "1st quarter", label: "1st quarter" },
        { value: "2nd quarter", label: "2nd quarter" },
        { value: "3rd quarter", label: "3rd quarter" },
        { value: "4th quarter", label: "4th quarter" },
        { value: "Finished", label: "Finished" },
        { value: "Extra time", label: "Extra time" },
        { value: "Finished AET", label: "Finished AET" },
        { value: "Not started yet", label: "NSY" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Deleted", label: "Deleted" },
        { value: "Full time", label: "Full time" },
        { value: "Break", label: "Break" },
        { value: "No info yet", label: "No info yet" },
        { value: "Extra time", label: "Extra time" },
        { value: "Postponed", label: "Postponed" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
      ];
      break;
    case "american-football":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st quarter", label: "1st quarter" },
        { value: "2nd quarter", label: "2nd quarter" },
        { value: "3rd quarter", label: "3rd quarter" },
        { value: "4th quarter", label: "4th quarter" },
        { value: "Extra time", label: "Extra time" },
        { value: "Finished AET", label: "Finished AET" },
        { value: "Finished", label: "Finished" },
        { value: "Break", label: "Break" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "No info yet", label: "No info yet" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
      ];
      break;
    case "handball":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st period", label: "1st period" },
        { value: "Half time", label: "Half time" },
        { value: "2nd period", label: "2nd period" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "To finish", label: "To finish" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Extra time", label: "Extra time" },
        { value: "Penalties", label: "Penalties" },
        { value: "Finished AP", label: "Finished After Penalties" },
        { value: "Finished AET", label: "Finished After Extra Time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
        { value: "Unknown", label: "Unknown" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
      ];
      break;
    case "tennis":
      options = [
        { value: "First set", label: "First set" },
        { value: "Second set", label: "Second set" },
        { value: "Third set", label: "Third set" },
        { value: "Fourth set", label: "Fourth set" },
        { value: "Fifth set", label: "Fifth set" },
        { value: "Finished", label: "Finished" },
        { value: "Not started yet", label: "NSY" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Postponed", label: "Postponed" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Retired", label: "Retired" },
        { value: "Walkover", label: "Walkover" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Unknown", label: "Unknown" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
      ];
      break;
    case "table-tennis":
      options = [
        { label: "First game", value: "First game" },
        { label: "Second game", value: "Second game" },
        { label: "Third game", value: "Third game" },
        { label: "Fourth game", value: "Fourth game" },
        { label: "Fifth game", value: "Fifth game" },
        { label: "Sixth game", value: "Sixth game" },
        { label: "Seventh game", value: "Seventh game" },
        { label: "Unknown", value: "Unknown" },
        { label: "Interrupted", value: "Interrupted" },
        { label: "Retired", value: "Retired" },
        { label: "Abandoned", value: "Abandoned" },
        { label: "Result only", value: "Result only" },
        { label: "Deleted", value: "Deleted" },
        { label: "Delayed", value: "Delayed" },
        { label: "Awaiting info", value: "Awaiting info" },
        { label: "Not started yet", value: "Not started yet" },
        { label: "Cancelled", value: "Cancelled" },
        { label: "Postponed", value: "Postponed" },
        { label: "No info yet", value: "No info yet" },
        { label: "Walkover", value: "Walkover" },
        { label: "Finished", value: "Finished" },
        { label: "Awarded", value: "Awarded" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
      ];
      break;
    case "hockey":
      options = [
        { value: "Not started yet", label: "Not started yet " },
        { value: "1st period", label: "1st period" },
        { value: "2nd period", label: "2nd period" },
        { value: "3rd period", label: "3rd period" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Extra time", label: "Extra time" },
        { value: "Penalties", label: "Penalties" },
        { value: "Result only", label: "Result Only" },
        { value: "2nd intermission", label: "2nd intermission" },
        { value: "Timeout", label: "Timeout" },
        { value: "Awarded", label: "Awarded" },
        { value: "ET intermission", label: "ET intermission" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "1st intermission", label: "1st intermission" },
        { value: "Postponed", label: "Postponed" },
        { value: "Finished AP", label: "Finished AP" },
        { value: "Finished AET", label: "Finished AET" },
        { value: "Unknown", label: "Unknown" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
      ];
      break;
    case "volleyball":
      options = [
        { value: "Not started yet", label: "Not started yet" },
        { value: "1st set", label: "1st set" },
        { value: "2nd set", label: "2nd set" },
        { value: "3rd set", label: "3rd set" },
        { value: "4th set", label: "4th set" },
        { value: "5th set", label: "5th set" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result Only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Postponed", label: "Postponed" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Unknown", label: "Unknown" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
      ];
      break;
    case "rugby-league":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st half", label: "1st half" },
        { value: "Half time", label: "Half time" },
        { value: "2nd half", label: "2nd half" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "To finish", label: "To finish" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "ET 1st half", label: "Extra Time First Half" },
        { value: "ET 2nd half", label: "Extra Time Second Half" },
        { value: "Penalties", label: "Penalties" },
        { value: "Finished AP", label: "Finished After Penalties" },
        { value: "Finished AET", label: "Finished After Extra Time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
        { value: "Unknown", label: "Unknown" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
        { value: "Awaiting info", label: "Awaiting info" },
      ];
      break;
    case "rugby-union":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st half", label: "1st half" },
        { value: "Half time", label: "Half time" },
        { value: "2nd half", label: "2nd half" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "To finish", label: "To finish" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "ET 1st half", label: "Extra Time First Half" },
        { value: "ET 2nd half", label: "Extra Time Second Half" },
        { value: "Penalties", label: "Penalties" },
        { value: "Finished AP", label: "Finished After Penalties" },
        { value: "Finished AET", label: "Finished After Extra Time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
        { value: "Unknown", label: "Unknown" },
        { value: "In Progress", label: "In Progress" },
        { value: "Upcoming", label: "Upcoming" },
        { value: "Active", label: "Active" },
        { value: "Awaiting info", label: "Awaiting info" },
      ];
      break;
    case "baseball":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "In progress", label: "In progress" },
        { value: "Finished", label: "Finished" },
        { value: "Finished AEI", label: "Finished AEI" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Postponed", label: "Postponed" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Awaiting info", label: "Awaiting info" },
        { value: "Inning end", label: "Inning end" },
      ];
      break;
    case "golf":
      options = [
      { value: "In Progress", label: "In Progress" },
      { value: "Finished", label: "Finished" },
      { value: "Not started yet", label: "Not started yet" },
      { value: "Abandoned", label: "Abandoned" },
      { value: "Result only", label: "Result only" },
      { value: "Cancelled", label: "Cancelled" },
      { value: "Interrupted", label: "Interrupted" },
      { value: "Awarded", label: "Awarded" },
      { value: "Delayed", label: "Delayed" },
      { value: "Postponed", label: "Postponed" },
      { value: "Deleted", label: "Deleted" },
      { value: "End of round", label: "End of round" },
      { value: "Awaiting info", label: "Awaiting info" },
      { value: "No info yet", label: "No info yet" },

      { value: "1st Round", label: "1st Round" },
      { value: "1st Round Finished", label: "1st Round Finished" },
      { value: "2nd Round", label: "2nd Round" },
      { value: "2nd Round Finished", label: "2nd Round Finished" },
      { value: "3rd Round", label: "3rd Round" },
      { value: "3rd Round Finished", label: "3rd Round Finished" },
      { value: "4th Round", label: "4th Round" },
      { value: "4th Round Finished", label: "4th Round Finished" },
      { value: "5th Round", label: "5th Round" },
      { value: "5th Round Finished", label: "5th Round Finished" },

      { value: "Unknown", label: "Unknown" }
      ];
    default:
      break;
  }

  return options;
};

export const getMatchStatusOptions = (sport) => {
  let options = [];

  switch (sport) {
    case "football":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st half", label: "1st half" },
        { value: "Half time", label: "Half time" },
        { value: "2nd half", label: "2nd half" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "To finish", label: "To finish" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "ET 1st half", label: "Extra Time First Half" },
        { value: "ET 2nd half", label: "Extra Time Second Half" },
        { value: "Penalties", label: "Penalties" },
        { value: "Finished AP", label: "Finished After Penalties" },
        { value: "Finished AET", label: "Finished After Extra Time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
        { value: "Unknown", label: "Unknown" },
        { value: "Awaiting info", label: "Awaiting info" },
      ];
      break;
    case "basketball":
      options = [
        { value: "1st quarter", label: "1st quarter" },
        { value: "2nd quarter", label: "2nd quarter" },
        { value: "3rd quarter", label: "3rd quarter" },
        { value: "4th quarter", label: "4th quarter" },
        { value: "Finished", label: "Finished" },
        { value: "Extra time", label: "Extra time" },
        { value: "Finished AET", label: "Finished AET" },
        { value: "Not started yet", label: "NSY" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Deleted", label: "Deleted" },
        { value: "Full time", label: "Full time" },
        { value: "Break", label: "Break" },
        { value: "No info yet", label: "No info yet" },
        { value: "Extra time", label: "Extra time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Awarded", label: "Awarded" },
      ];
      break;
    case "american-football":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st quarter", label: "1st quarter" },
        { value: "2nd quarter", label: "2nd quarter" },
        { value: "3rd quarter", label: "3rd quarter" },
        { value: "4th quarter", label: "4th quarter" },
        { value: "Extra time", label: "Extra time" },
        { value: "Finished AET", label: "Finished AET" },
        { value: "Finished", label: "Finished" },
        { value: "Break", label: "Break" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "No info yet", label: "No info yet" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
      ];
      break;
    case "handball":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st period", label: "1st period" },
        { value: "Half time", label: "Half time" },
        { value: "2nd period", label: "2nd period" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "To finish", label: "To finish" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Extra time", label: "Extra time" },
        { value: "Penalties", label: "Penalties" },
        { value: "Finished AP", label: "Finished After Penalties" },
        { value: "Finished AET", label: "Finished After Extra Time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
        { value: "Unknown", label: "Unknown" },
      ];
      break;
    case "tennis":
      options = [
        { value: "First set", label: "First set" },
        { value: "Second set", label: "Second set" },
        { value: "Third set", label: "Third set" },
        { value: "Fourth set", label: "Fourth set" },
        { value: "Fifth set", label: "Fifth set" },
        { value: "Finished", label: "Finished" },
        { value: "Not started yet", label: "NSY" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Postponed", label: "Postponed" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Retired", label: "Retired" },
        { value: "Walkover", label: "Walkover" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Unknown", label: "Unknown" },
      ];
      break;
    case "hockey":
      options = [
        { value: "Not started yet", label: "Not started yet " },
        { value: "1st period", label: "1st period" },
        { value: "2nd period", label: "2nd period" },
        { value: "3rd period", label: "3rd period" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Extra time", label: "Extra time" },
        { value: "Penalties", label: "Penalties" },
        { value: "Result only", label: "Result Only" },
        { value: "2nd intermission", label: "2nd intermission" },
        { value: "Timeout", label: "Timeout" },
        { value: "Awarded", label: "Awarded" },
        { value: "ET intermission", label: "ET intermission" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "1st intermission", label: "1st intermission" },
        { value: "Postponed", label: "Postponed" },
        { value: "Finished AP", label: "Finished AP" },
        { value: "Finished AET", label: "Finished AET" },
        { value: "Unknown", label: "Unknown" },
      ];
      break;
    case "volleyball":
      options = [
        { value: "Not started yet", label: "Not started yet" },
        { value: "1st set", label: "1st set" },
        { value: "2nd set", label: "2nd set" },
        { value: "3rd set", label: "3rd set" },
        { value: "4th set", label: "4th set" },
        { value: "5th set", label: "5th set" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result Only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Postponed", label: "Postponed" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Unknown", label: "Unknown" },
      ];
      break;
    case "rugby-league":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st half", label: "1st half" },
        { value: "Half time", label: "Half time" },
        { value: "2nd half", label: "2nd half" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "To finish", label: "To finish" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "ET 1st half", label: "Extra Time First Half" },
        { value: "ET 2nd half", label: "Extra Time Second Half" },
        { value: "Penalties", label: "Penalties" },
        { value: "Finished AP", label: "Finished After Penalties" },
        { value: "Finished AET", label: "Finished After Extra Time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
        { value: "Unknown", label: "Unknown" },
        { value: "Awaiting info", label: "Awaiting info" },
      ];
      break;
    case "table-tennis":
      options = [
        { label: "Not started yet", value: "Not started yet" },
        { label: "First game", value: "First game" },
        { label: "Second game", value: "Second game" },
        { label: "Third game", value: "Third game" },
        { label: "Fourth game", value: "Fourth game" },
        { label: "Fifth game", value: "Fifth game" },
        { label: "Sixth game", value: "Sixth game" },
        { label: "Seventh game", value: "Seventh game" },
        { label: "Unknown", value: "Unknown" },
        { label: "Interrupted", value: "Interrupted" },
        { label: "Retired", value: "Retired" },

        { label: "Abandoned", value: "Abandoned" },
        { label: "Result only", value: "Result only" },

        { label: "Deleted", value: "Deleted" },

        { label: "Delayed", value: "Delayed" },

        { label: "Awaiting info", value: "Awaiting info" },

        { label: "Cancelled", value: "Cancelled" },
        { label: "Postponed", value: "Postponed" },

        { label: "No info yet", value: "No info yet" },
        { label: "Walkover", value: "Walkover" },
        { label: "Finished", value: "Finished" },
        { label: "Awarded", value: "Awarded" },
      ];
      break;
    case "rugby-union":
      options = [
        { value: "Not started yet", label: "NSY" },
        { value: "1st half", label: "1st half" },
        { value: "Half time", label: "Half time" },
        { value: "2nd half", label: "2nd half" },
        { value: "Finished", label: "Finished" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "To finish", label: "To finish" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "ET 1st half", label: "Extra Time First Half" },
        { value: "ET 2nd half", label: "Extra Time Second Half" },
        { value: "Penalties", label: "Penalties" },
        { value: "Finished AP", label: "Finished After Penalties" },
        { value: "Finished AET", label: "Finished After Extra Time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Full time", label: "Full time" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
        { value: "Unknown", label: "Unknown" },
        { value: "Awaiting info", label: "Awaiting info" },
      ];
      break;
    case "baseball":
      options = [
        { value: "In progress", label: "In progress" },
        { value: "Finished", label: "Finished" },
        { value: "Finished AEI", label: "Finished AEI" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "No info yet", label: "No info yet" },
        { value: "Not started yet", label: "Not started yet" },
        { value: "Half time", label: "Half time" },
        { value: "Postponed", label: "Postponed" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Deleted", label: "Deleted" },
        { value: "Awaiting info", label: "Awaiting info" },
        { value: "Inning end", label: "Inning end" },
      ];
    case "golf":
      options = [
       { value: "In Progress", label: "In Progress" },
        { value: "Finished", label: "Finished" },
        { value: "Not started yet", label: "Not started yet" },
        { value: "Abandoned", label: "Abandoned" },
        { value: "Result only", label: "Result only" },
        { value: "Cancelled", label: "Cancelled" },
        { value: "Interrupted", label: "Interrupted" },
        { value: "Awarded", label: "Awarded" },
        { value: "Delayed", label: "Delayed" },
        { value: "Postponed", label: "Postponed" },
        { value: "Deleted", label: "Deleted" },
        { value: "End of round", label: "End of round" },
        { value: "Awaiting info", label: "Awaiting info" },
        { value: "No info yet", label: "No info yet" },

        { value: "1st Round", label: "1st Round" },
        { value: "1st Round Finished", label: "1st Round Finished" },
        { value: "2nd Round", label: "2nd Round" },
        { value: "2nd Round Finished", label: "2nd Round Finished" },
        { value: "3rd Round", label: "3rd Round" },
        { value: "3rd Round Finished", label: "3rd Round Finished" },
        { value: "4th Round", label: "4th Round" },
        { value: "4th Round Finished", label: "4th Round Finished" },
        { value: "5th Round", label: "5th Round" },
        { value: "5th Round Finished", label: "5th Round Finished" },

        { value: "Unknown", label: "Unknown" }
      ];
    case "mma":
      options = [
        { label: "Not started yet", value: "Not started yet" },

        { label: "1st round", value: "1st round" },
        { label: "2nd round", value: "2nd round" },
        { label: "3rd round", value: "3rd round" },
        { label: "4th round", value: "4th round" },
        { label: "5th round", value: "5th round" },

        { label: "1st break", value: "1st break" },
        { label: "2nd break", value: "2nd break" },
        { label: "3rd break", value: "3rd break" },
        { label: "4th break", value: "4th break" },

        { label: "Interrupted", value: "Interrupted" },
        { label: "Finished", value: "Finished" },
        { label: "Cancelled", value: "Cancelled" },
        { label: "Deleted", value: "Deleted" }
      ];
    default:
      break;
  }

  return options;
};

export const getLiveStatuses = (sport) => {
  let options = [];

  switch (sport) {
    case "football":
      options = [
        { value: "1st half", label: "1st half" },
        { value: "Half time", label: "Half time" },
        { value: "2nd half", label: "2nd half" },
        { value: "ET 1st half", label: "Extra Time First Half" },
        { value: "ET 2nd half", label: "Extra Time Second Half" },
        { value: "Penalties", label: "Penalties" },
        { value: "Full time", label: "Full time" },
        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
        { value: "Awaiting info", label: "Awaiting info" },
      ];
      break;
    case "basketball":
      options = [
        { value: "1st quarter", label: "1st quarter" },
        { value: "2nd quarter", label: "2nd quarter" },
        { value: "3rd quarter", label: "3rd quarter" },
        { value: "4th quarter", label: "4th quarter" },
        { value: "Break", label: "Break" },
        { value: "Extra time", label: "Extra time" },
        { value: "Full time", label: "Full time" },
      ];
      break;

    case "american-football":
      options = [
        { value: "1st quarter", label: "1st quarter" },
        { value: "2nd quarter", label: "2nd quarter" },
        { value: "3rd quarter", label: "3rd quarter" },
        { value: "4th quarter", label: "4th quarter" },
        { value: "Extra time", label: "Extra time" },
        { value: "Full time", label: "Full time" },
      ];
      break;
    case "handball":
      options = [
        { value: "1st period", label: "1st period" },
        { value: "Half time", label: "Half time" },
        { value: "2nd period", label: "2nd period" },
        { value: "To finish", label: "To finish" },

        { value: "Extra time", label: "Extra time" },
        { value: "Penalties", label: "Penalties" },

        { value: "Full time", label: "Full time" },

        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
      ];
      break;
    case "tennis":
      options = [
        { value: "First set", label: "First set" },
        { value: "Second set", label: "Second set" },
        { value: "Third set", label: "Third set" },
        { value: "Fourth set", label: "Fourth set" },
        { value: "Fifth set", label: "Fifth set" },
      ];
      break;
    case "hockey":
      options = [
        { value: "1st period", label: "1st period" },
        { value: "2nd period", label: "2nd period" },
        { value: "3rd period", label: "3rd period" },

        { value: "Extra time", label: "Extra time" },
        { value: "Penalties", label: "Penalties" },

        { value: "2nd intermission", label: "2nd intermission" },

        { value: "ET intermission", label: "ET intermission" },

        { value: "Full time", label: "Full time" },

        { value: "1st intermission", label: "1st intermission" },
      ];
      break;
    case "volleyball":
      options = [
        { value: "1st set", label: "1st set" },
        { value: "2nd set", label: "2nd set" },
        { value: "3rd set", label: "3rd set" },
        { value: "4th set", label: "4th set" },
        { value: "5th set", label: "5th set" },
      ];
      break;
    case "rugby-league":
      options = [
        { value: "1st half", label: "1st half" },
        { value: "Half time", label: "Half time" },
        { value: "2nd half", label: "2nd half" },

        { value: "ET 1st half", label: "Extra Time First Half" },
        { value: "ET 2nd half", label: "Extra Time Second Half" },
        { value: "Penalties", label: "Penalties" },

        { value: "Full time", label: "Full time" },

        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
      ];
      break;

    case "rugby-union":
      options = [
        { value: "1st half", label: "1st half" },
        { value: "Half time", label: "Half time" },
        { value: "2nd half", label: "2nd half" },

        { value: "ET 1st half", label: "Extra Time First Half" },
        { value: "ET 2nd half", label: "Extra Time Second Half" },
        { value: "Penalties", label: "Penalties" },

        { value: "Full time", label: "Full time" },

        { value: "Extra time half time", label: "Extra Time Half Time" },
        { value: "Extra time full time", label: "Extra Time Full Time" },
      ];
      break;
    case "table-tennis":
      options = [
        { label: "First game", value: "First game" },
        { label: "Second game", value: "Second game" },
        { label: "Third game", value: "Third game" },
        { label: "Fourth game", value: "Fourth game" },
        { label: "Fifth game", value: "Fifth game" },
        { label: "Sixth game", value: "Sixth game" },
        { label: "Seventh game", value: "Seventh game" },
      ];
      break;

    default:
      break;
  }

  return options?.map((op) => op?.value);
};

export const getMatchOfficialPositions = (sport) => {
  let options = [];

  switch (sport) {
    case "football":
      options = [
        { value: "Referee", label: "Referee" },
        { value: "Delegate", label: "Delegate" },
      ];
      break;
    case "basketball":
      options = [
        { value: "Referee", label: "Referee" },
        { value: "Umpire", label: "Umpire" },
      ];
      break;

    case "american-football":
      options = [];
      break;
    case "handball":
      options = [];
      break;
    case "tennis":
      options = [
        { value: "Referee", label: "Referee" },
        { value: "Chair umpire", label: "Chair umpire" },
        { value: "Line umpire", label: "Line umpire" },
      ];
      break;
    case "hockey":
      options = [
        { value: "Referee", label: "Referee" },
        { value: "Linesman", label: "Linesman" },
      ];
      break;
    case "volleyball":
      options = [];
      break;
    case "rugby-league":
      options = [
        { value: "Referee", label: "Referee" },
        { value: "Assistant referee", label: "Assistant referee" },
        {
          value: "Television match official",
          label: "Television match official",
        },
      ];
      break;

    case "rugby-union":
      options = [
        { value: "Referee", label: "Referee" },
        { value: "Assistant referee", label: "Assistant referee" },
        {
          value: "Television match official",
          label: "Television match official",
        },
      ];
      break;

    default:
      break;
  }

  return options?.map((op) => op?.value);
};

export const getMatchDateTimeScope = (sport) => {
  let options = [];

  switch (sport) {
    case "football":
      options = [
        { value: "1st half", label: "1st half" },
        { value: "2nd half", label: "2nd half" },
        { value: "1st extra time", label: "1st extra time" },
        { value: "2nd extra time", label: "2nd extra time" },
        { value: "Finished", label: "Finished" },
      ];
      break;
    case "rugby-league":
      options = [
        { value: "1st half", label: "1st half" },
        { value: "2nd half", label: "2nd half" },
        {
          value: "Television match official",
          label: "Television match official",
        },
      ];
      break;

    case "rugby-union":
      options = [
        { value: "1st half", label: "1st half" },
        { value: "2nd half", label: "2nd half" },
        {
          value: "Television match official",
          label: "Television match official",
        },
      ];
      break;

    default:
      break;
  }

  return options?.map((op) => op?.value);
};

export const formatDateTime = (datetimeString) => {
  const date = new Date(datetimeString);

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const hours = date.getHours();
  const minutes = date.getMinutes();

  const dayFormatted = day < 10 ? "0" + day : day;
  const monthFormatted = month < 10 ? "0" + month : month;

  const hoursFormatted = hours < 10 ? "0" + hours : hours;
  const minutesFormatted = minutes < 10 ? "0" + minutes : minutes;

  const formattedDateTime = `${dayFormatted}-${monthFormatted} ${hoursFormatted}:${minutesFormatted}`;

  return formattedDateTime;
};

export const dateTimeStamp = (date, time) => {
  var dateANdTime = `${date} ${time}`;
  return moment.utc(dateANdTime).format();
};

export const TimeStamp = (date) => {
  var _date = `${date}`;
  return moment.utc(_date).format();
};

export const setOvrLimit = (data) => {
  switch (data) {
    case "ODI":
      return 50;
    case "T20":
      return 20;
    case "WODI":
      return 90;
  }
};

export const popDataa = (url, query) => {
  query
    ? window.open(
        `${url}/?${query}`,
        "newWin",
        "left=0,top=50,scrollbars=yes,menubar=no,toolbar=0,resizable=0,scrollbars=1,width=700px,height=700px"
      )
    : window.open(
        `${url}`,
        "newWin",
        "left=0,top=50,scrollbars=yes,menubar=no,toolbar=0,resizable=0,scrollbars=1,width=700px,height=700px"
      );
};

export { formatPresentCustomDate, formatCustomDateToApi };

export const GetUTCDateTime = (localdate) => {
  if (localdate) {
    let utcdatestr = new Date(localdate).toISOString().split("T");
    let datestr = utcdatestr[0];
    let timestr = utcdatestr[1];

    let year = datestr.split("-")[0];
    let month = datestr.split("-")[1];
    let day = datestr.split("-")[2];

    let hour = timestr.split(":")[0];
    let mint = timestr.split(":")[1];
    let seconds = timestr.split(":")[2].includes(".")
      ? timestr.split(":")[2].split(".")[0]
      : timestr.split(":")[2].replace("Z", "");
    let utcDateTime = new Date(
      year,
      Number(month) - 1,
      day,
      hour,
      mint,
      seconds
    );
    return utcDateTime;
  }
  var UtcDateString = new Date().toISOString();
  return UtcDateString;
};

export const SignIn = async (data) => {
  const res = await request({
    url: "",
    method: "post",
    data: {},
  });

  console.log(res);
};

export const getSportId = (name) => {
  let id = "";
  switch (name) {
    case "football":
      id = "1";
      break;
    case "basketball":
      id = "23";
      break;
    case "hockey":
      id = "5";
      break;
    case "rugby-league":
      id = "80";
      break;
    case "rugby-union":
      id = "29";
      break;
    case "tennis":
      id = "2";
      break;
    case "handball":
      id = "5";
      break;
    case "volleyball":
      id = "5";
      break;
    case "american-football":
      id = "5";
      break;
    case "baseball":
      id = "26";
      break;
    case "golf":
      id = "3";
      break;
    case "mma":
      id = "75";
      break;
    case "motorsport":
      id = "22";
      break;
    default:
      id = "1";
      break;
  }
  return id;
};

export const history = {
  navigate: null,
  location: null,
};

export const liveStatusCodes = {
  football: [
    "1stHalf",
    "halfTime",
    "2ndHalf",
    "ET1stHalf",
    "ET2ndHalf",
    "penalties",
  ],
  tennis: [],
  basketball: [],
  volleyball: [],
  hockey: [],
  handball: [],
  "american-football": [],
  "rugby-league": [],
  "rugby-union": [],
};

export const extractLastTwoSegments = (path) => {
  if (typeof path !== "string") return "";

  const segments = path.split("/").filter(Boolean); // remove empty strings
  const len = segments.length;

  if (len < 2) return ""; // not enough segments to extract

  const lastTwo = segments.slice(len - 2);
  return "/" + lastTwo.join("/");
};

export const DOTNETURL = {
  url1: "https://dotnet6.6lgx.com",
  url2: "https://datafeed.sixlogics.com",
};


export const getCurrentUTCDateTime = () => {
  return new Date()?.toISOString();
}

// Scopes read chronologically (Total, First half, Second half ...), so group the match
// stats listing by the sport's declared scope order rather than alphabetically.
// Scopes not present in the enum fall to the bottom.
export const sortStatsByScope = (stats, scopeOptions) =>
  orderBy(stats, [
    (s) => {
      const idx = scopeOptions?.findIndex((o) => o?.value === s?.scope);
      return idx === undefined || idx < 0 ? Number.MAX_SAFE_INTEGER : idx;
    },
    "type",
  ]);